import React, { useEffect, useRef, useState } from 'react';
import { useProductFinancing } from '../../hooks/useProductFinancing';
import { useHeroSlides } from '../../hooks/useHeroSlides';
import { Link } from 'react-router-dom';
import { Sparkles, Briefcase, Building2, Mountain, Battery, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

const ROTATE_MS = 6500;

interface SpecMini {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  label: string;
}
interface ModelData {
  id: string;
  productId: string;
  name: string;
  tagline: string;
  description: string;
  image: string;
  lifestyle?: boolean;
  specsMinimal: SpecMini[];
  specs: { label: string; value: string }[];
  primaryCta: { label: string; to: string };
  accent: string;
  basePrice: number;
  priceLabel?: string;
  priceAmount?: string;
  priceCurrency?: string;
  priceUnit?: string;
  priceSubtitle?: string;
}

const MODELS: ModelData[] = [
  {
    id: 'sunu',
    productId: 'sunu',
    name: 'SUNU',
    tagline: 'La mobilité professionnelle intelligente.',
    description: "Pensée pour les pros qui roulent toute la journée : plus d'autonomie, moins de dépenses, zéro carburant.",
    image: '/sunu-lifestyle.jpg',
    lifestyle: true,
    specsMinimal: [
      { icon: Briefcase, label: 'Conçue pour les pros' },
      { icon: Battery, label: 'Autonomie 100 km' },
    ],
    specs: [
      { label: 'Autonomie', value: '100 km' },
      { label: 'Vitesse', value: '80 km/h' },
      { label: 'Recharge', value: '3 min' },
    ],
    primaryCta: { label: 'Découvrir SUNU', to: '/products/sunu' },
    accent: 'primary',
    basePrice: 850000,
  },
  {
    id: 'teranga-plus',
    productId: 'teranga-plus',
    name: 'TERANGA+',
    tagline: 'La moto électrique pour la ville.',
    description: 'Légère et maniable pour circuler en ville. Réservez la vôtre dès 50 000 FCFA.',
    image: '/teranga-plus-lifestyle.jpg',
    lifestyle: true,
    specsMinimal: [
      { icon: Building2, label: 'Pensée pour la ville' },
      { icon: Battery, label: 'Autonomie 110 km' },
    ],
    specs: [
      { label: 'Autonomie', value: '110 km' },
      { label: 'Vitesse', value: '50 km/h' },
      { label: 'Poids', value: '88 kg' },
    ],
    primaryCta: { label: 'Découvrir Teranga+', to: '/products/teranga-plus' },
    accent: 'primary',
    basePrice: 850000,
  },
  {
    id: 'omega',
    productId: 'omega',
    name: 'OMEGA',
    tagline: 'La puissance électrique pour les zones rurales.',
    description: 'Faite pour les pistes et les longues distances : elle encaisse les terrains difficiles sans broncher.',
    image: '/omega-lifestyle.jpg',
    lifestyle: true,
    specsMinimal: [
      { icon: Mountain, label: 'Conçue pour zones rurales' },
      { icon: Battery, label: 'Autonomie étendue' },
    ],
    specs: [
      { label: 'Autonomie', value: '120 km' },
      { label: 'Vitesse', value: '90 km/h' },
      { label: 'Charge', value: '4 min' },
    ],
    primaryCta: { label: 'Découvrir OMEGA', to: '/products/omega' },
    accent: 'primary',
    basePrice: 850000,
  },
];

const ModelShowcase: React.FC = () => {
  const finMap = useProductFinancing();
  const slides = useHeroSlides(MODELS);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (paused) return;
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    timer.current = window.setInterval(() => setIndex((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [paused]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + slides.length) % slides.length);
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % slides.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const prev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setIndex((i) => (i + 1) % slides.length);

  const model = slides[index];
  const isPrimary = model.accent === 'primary';
  const price =
    (model.priceLabel || model.priceAmount || model.priceSubtitle)
      ? { label: model.priceLabel || '', amount: model.priceAmount || '', currency: model.priceCurrency || 'FCFA', unit: model.priceUnit || '', subtitle: model.priceSubtitle || '' }
      : model.id === 'teranga-plus'
        ? { label: 'Réservation', amount: '50 000', currency: 'FCFA', unit: 'acompte', subtitle: "PAY MOLO jusqu'à livraison" }
        : { label: 'À partir de', amount: '2 000', currency: 'FCFA', unit: '/ jour', subtitle: 'Devenez propriétaire en 24 mois' };

  // Taille du titre desktop adaptée à la longueur du nom (évite que « TERANGA+ » déborde sous l'image).
  const desktopTitleSize =
    model.name.length > 7 ? 'text-6xl xl:text-[4.75rem]'
    : model.name.length > 5 ? 'text-7xl xl:text-[6rem]'
    : 'text-7xl xl:text-[7.5rem]';
  const taglineHead = model.tagline.split(' ').slice(0, -2).join(' ');
  const taglineTail = model.tagline.split(' ').slice(-2).join(' ');

  return (
    <section
      translate="no"
      className="notranslate relative overflow-hidden bg-white pt-3 pb-6 sm:pt-6 sm:pb-10 lg:pt-8 lg:pb-12"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      aria-label="Découverte des modèles Proxy"
    >
      <style>{`
        @keyframes msProgress { from { width: 0%; } to { width: 100%; } }
        .ms-progress { animation: msProgress ${ROTATE_MS}ms linear forwards; }
        @media (prefers-reduced-motion: reduce) { .ms-progress { animation: none; width: 100%; } }
      `}</style>
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-50/60 via-white to-white" />
        <div className="absolute top-1/3 left-[5%] h-72 w-72 rounded-full bg-primary-500/[0.05] blur-3xl" />
        <div className="absolute top-1/2 right-[5%] h-96 w-96 rounded-full bg-neutral-900/[0.025] blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop */}
        <div className="hidden lg:block">
          <div className="mb-10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-primary-600" strokeWidth={2.5} />
              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-neutral-500">
                <span className="text-primary-600">Proxy Emobility</span> · Mobilité africaine
              </span>
            </div>
            <div className="flex items-baseline gap-2 tabular-nums">
              <span className="text-sm font-black tracking-tight text-primary-600">{String(index + 1).padStart(2, '0')}</span>
              <span className="text-sm font-bold text-neutral-300">/</span>
              <span className="text-sm font-black tracking-tight text-primary-600">{String(slides.length).padStart(2, '0')}</span>
            </div>
          </div>
          <div className="relative grid grid-cols-12 items-center gap-8">
            <div className="col-span-5 xl:col-span-5">
              <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-primary-100 bg-primary-50/80 px-3.5 py-1.5 backdrop-blur-sm">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-500 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary-500" />
                </span>
                <span className="text-xs font-black uppercase tracking-[0.22em] text-primary-700">100% électrique</span>
              </div>
              <h1 key={`title-${model.id}`} className={`mb-6 ${desktopTitleSize} font-black leading-[0.92] tracking-[-0.045em] text-neutral-900`}>
                {model.name}
              </h1>
              <h2 className="mb-5 text-2xl xl:text-3xl font-bold leading-[1.15] tracking-[-0.015em] text-neutral-900">
                {taglineHead} <span className={isPrimary ? 'text-primary-600' : 'text-neutral-900'}>{taglineTail}</span>
              </h2>
              <p className="mb-7 max-w-md text-base leading-relaxed text-neutral-600">{model.description}</p>
              {(model.id === 'sunu' || model.id === 'omega') && finMap[model.id] && (
                <div className="mb-7 flex flex-wrap items-center gap-2">
                  {finMap[model.id].paymolo_enabled && (<span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-bold text-neutral-700"><img src="/images/paymolo-logo.png" alt="" className="h-3.5 w-auto" /> Pay Molo disponible</span>)}
                  {finMap[model.id].paygo_enabled && (<span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-bold text-neutral-700"><img src="/images/paygo-logo.png" alt="" className="h-3.5 w-auto" /> PayGo disponible</span>)}
                </div>
              )}
              {model.id === 'teranga-plus' && (
                <div className="mb-7 flex items-center gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-400">Partenaires</span>
                  <img src="/images/paymolo-logo.png" alt="Pay Molo" className="h-6 w-auto" />
                  <img src="/images/prime-logo.png" alt="Prime Sénégal" className="h-4 w-auto" />
                </div>
              )}
              <div className="mb-8 flex flex-wrap gap-x-8 gap-y-3">
                {model.specsMinimal.map((spec, m) => {
                  const Icon = spec.icon;
                  return (
                    <div key={m} className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-50 ring-1 ring-neutral-200/80">
                        <Icon className="h-4 w-4 text-neutral-700" strokeWidth={2} />
                      </span>
                      <span className="text-sm font-bold text-neutral-900">{spec.label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Link
                  to={model.primaryCta.to}
                  className="group inline-flex items-center justify-between gap-3 rounded-2xl bg-neutral-900 px-7 py-5 text-base font-bold text-white shadow-[0_14px_30px_-12px_rgba(0,0,0,0.45)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary-600 hover:shadow-[0_18px_40px_-12px_rgba(24,153,78,0.55)]"
                >
                  <span>{model.primaryCta.label}</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/products"
                  className="group inline-flex items-center justify-between gap-3 rounded-2xl border border-neutral-300 bg-white px-7 py-5 text-base font-bold text-neutral-900 transition-all duration-300 hover:-translate-y-0.5 hover:border-neutral-900 hover:shadow-[0_14px_30px_-12px_rgba(0,0,0,0.1)]"
                >
                  <span>Voir tous les modèles</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
            <div className="relative col-span-7 xl:col-span-7">
              <div className="relative flex h-[480px] xl:h-[560px] items-end justify-center">
                <div key={`img-${model.id}`} className="relative z-10 h-full w-full animate-fade-in overflow-hidden rounded-[2rem] shadow-[0_30px_60px_-25px_rgba(0,0,0,0.4)] ring-1 ring-neutral-200/70">
                  {model.lifestyle ? (
                    <img src={model.image} alt={`Moto électrique Proxy ${model.name}`} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-neutral-50 via-white to-primary-50/40 p-10">
                      <img src={model.image} alt={`Moto électrique Proxy ${model.name}`} className="max-h-full max-w-full object-contain" style={{ mixBlendMode: 'multiply' }} />
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={prev}
                className="absolute left-0 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-[0_8px_24px_-8px_rgba(0,0,0,0.15)] ring-1 ring-neutral-200/80 transition-all duration-300 hover:-translate-y-[55%] hover:bg-neutral-50 hover:shadow-[0_14px_30px_-10px_rgba(0,0,0,0.2)]"
                aria-label="Modèle précédent"
              >
                <ChevronLeft className="h-5 w-5 text-neutral-700" strokeWidth={2.5} />
              </button>
              <button
                onClick={next}
                className="absolute right-0 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-[0_8px_24px_-8px_rgba(0,0,0,0.15)] ring-1 ring-neutral-200/80 transition-all duration-300 hover:-translate-y-[55%] hover:bg-neutral-50 hover:shadow-[0_14px_30px_-10px_rgba(0,0,0,0.2)]"
                aria-label="Modèle suivant"
              >
                <ChevronRight className="h-5 w-5 text-neutral-700" strokeWidth={2.5} />
              </button>
              <div className="absolute bottom-4 right-0 z-20 w-[260px] xl:w-[280px] rounded-2xl bg-white p-5 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.18)] ring-1 ring-neutral-200/80">
                <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary-50 ring-1 ring-primary-100">
                  {model.id === 'sunu' && <Briefcase className="h-4 w-4 text-primary-600" strokeWidth={2.2} />}
                  {model.id === 'teranga-plus' && <Building2 className="h-4 w-4 text-primary-600" strokeWidth={2.2} />}
                  {model.id === 'omega' && <Mountain className="h-4 w-4 text-primary-600" strokeWidth={2.2} />}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">{price.label}</div>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="text-3xl xl:text-[2rem] font-black tracking-tight text-primary-600 tabular-nums leading-none">{price.amount}</span>
                  <span className="text-base font-black tracking-tight text-primary-600">{price.currency}</span>
                  <span className="text-sm font-bold text-neutral-500">{price.unit}</span>
                </div>
                <div className="my-3 h-px bg-neutral-100" />
                <p className="mb-3 text-xs text-neutral-600">{price.subtitle}</p>
                <Link
                  to={model.primaryCta.to}
                  className="group flex items-center justify-center gap-2 rounded-xl bg-neutral-900 px-4 py-2.5 text-sm font-bold text-white transition-all duration-300 hover:bg-primary-600"
                >
                  Choisir
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2.5} />
                </Link>
              </div>
            </div>
          </div>
          <div className="mt-10 flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              {slides.map((m, i) => (
                <button
                  key={m.id}
                  onClick={() => setIndex(i)}
                  className={`relative h-1 overflow-hidden transition-all duration-300 ${i === index ? 'w-14 bg-neutral-200' : 'w-8 bg-neutral-200 hover:bg-neutral-300'}`}
                  style={{ borderRadius: '2px' }}
                  aria-label={`Voir le slide ${m.name}`}
                >
                  {i === index && (
                    <span
                      key={`ms-prog-${index}`}
                      className="ms-progress absolute inset-y-0 left-0 block bg-primary-500"
                      style={{ borderRadius: '2px', animationPlayState: paused ? 'paused' : 'running' }}
                    />
                  )}
                </button>
              ))}
            </div>
            <span className="text-[11px] font-black uppercase tracking-[0.22em] text-primary-600">{model.name}</span>
          </div>
        </div>

        {/* Mobile */}
        <div className="px-1 pb-2 lg:hidden">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3 w-3 text-primary-600" strokeWidth={2.5} />
              <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-neutral-500">Mobilité africaine</span>
            </div>
            <span className="text-[10px] font-bold tabular-nums text-neutral-400">
              {String(index + 1).padStart(2, '0')} <span className="text-neutral-300">/</span> {String(slides.length).padStart(2, '0')}
            </span>
          </div>
          <div className="mb-2.5 inline-flex items-center gap-1.5 rounded-full border border-primary-100 bg-primary-50/80 px-3 py-1">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-500 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary-500" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary-700">100% électrique</span>
          </div>
          <h1 className="mb-1.5 text-[2.75rem] font-black leading-[0.92] tracking-[-0.035em] text-neutral-900 sm:text-5xl">{model.name}</h1>
          <p className="mb-3 text-[15px] font-bold leading-[1.2] text-neutral-900 sm:text-base">
            {taglineHead} <span className={isPrimary ? 'text-primary-600' : 'text-neutral-900'}>{taglineTail}</span>
          </p>
          <Link
            to={model.primaryCta.to}
            className="group mb-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-neutral-900 px-5 py-3 text-sm font-bold text-white shadow-[0_12px_28px_-12px_rgba(0,0,0,0.4)] transition-all duration-300 active:scale-95"
          >
            {model.primaryCta.label}
            <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="relative mb-3 flex h-[200px] items-end justify-center sm:h-[240px]">
            <div key={`img-mobile-${model.id}`} className="relative z-10 h-full w-full overflow-hidden rounded-2xl shadow-[0_18px_36px_-18px_rgba(0,0,0,0.4)] ring-1 ring-neutral-200/70">
              {model.lifestyle ? (
                <img src={model.image} alt={`Moto électrique Proxy ${model.name}`} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-neutral-50 via-white to-primary-50/40 p-5">
                  <img src={model.image} alt={`Moto électrique Proxy ${model.name}`} className="max-h-full max-w-full object-contain" style={{ mixBlendMode: 'multiply' }} />
                </div>
              )}
            </div>
          </div>
          <div className="mb-3 grid grid-cols-3 gap-2 rounded-xl bg-neutral-50 px-2 py-2.5">
            {model.specs.map((spec, m) => (
              <div key={m} className="text-center">
                <div className="text-sm font-black tracking-tight text-neutral-900 sm:text-base">{spec.value}</div>
                <div className="mt-0.5 text-[8px] font-bold uppercase tracking-wider text-neutral-500">{spec.label}</div>
              </div>
            ))}
          </div>
          {(model.id === 'sunu' || model.id === 'omega') && finMap[model.id] && (
            <div className="mb-3 flex flex-wrap items-center justify-center gap-1.5">
              {finMap[model.id].paymolo_enabled && (<span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-bold text-neutral-700"><img src="/images/paymolo-logo.png" alt="" className="h-3 w-auto" /> Pay Molo</span>)}
              {finMap[model.id].paygo_enabled && (<span className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-bold text-neutral-700"><img src="/images/paygo-logo.png" alt="" className="h-3 w-auto" /> PayGo</span>)}
            </div>
          )}
          {model.id === 'teranga-plus' && (
            <div className="mb-3 flex items-center justify-center gap-3">
              <img src="/images/paymolo-logo.png" alt="Pay Molo" className="h-5 w-auto" />
              <img src="/images/prime-logo.png" alt="Prime Sénégal" className="h-3.5 w-auto" />
            </div>
          )}
          <div className="flex flex-col gap-2">
            <Link
              to="/products"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white px-5 py-3 text-sm font-bold text-neutral-900 transition-all duration-300 active:scale-95"
            >
              Voir tous les modèles
            </Link>
          </div>
          <div className="mt-3 flex items-center justify-center gap-2">
            {slides.map((m, i) => (
              <button
                key={m.id}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? 'w-8 bg-neutral-900' : 'w-1.5 bg-neutral-300 hover:bg-neutral-400'}`}
                aria-label={`Voir le slide ${m.name}`}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </section>
  );
};

export default ModelShowcase;
